import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Shield, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Truck, QrCode, Star, Clock, Award } from 'lucide-react';

const HERO_BG = 'https://customer-assets.emergentagent.com/job_6f167cb2-c653-4d56-91af-258001cc5e51/artifacts/48zqpn4v_Bilde%20til%20DC-Protect%20som%20viser%20belegg.png';
const STONE_IMG = 'https://customer-assets.emergentagent.com/job_6f167cb2-c653-4d56-91af-258001cc5e51/artifacts/a8kh0vi3_Bilde%20fra%20Busknes%20gravplass.png';

const KIT_CONTENTS = [
  '2 stk mikrofiberklut',
  '1 stk påføringssvamp',
  '1 flaske 20 ml forvask',
  '1 flaske DC-Protect 7,5 ml',
  '2 par hansker',
  'Garantinummer + QR-kode',
];

const STEPS = [
  { num: 1, title: 'Vask gravsteinen grundig', text: 'Bruk forvasken som følger med kittet og en mikrofiberklut. Skyll med rent vann og la det tørke helt.' },
  { num: 2, title: 'Påfør DC-Protect', text: 'Bruk svampen og påfør et tynt, jevnt lag av DC-Protect på lakkerte ikoner og skrift. Bruk hanskene som følger med.' },
  { num: 3, title: 'La det herde', text: 'La beskyttelsen herde i ca. 24 timer. Unngå regn og berøring i denne perioden.' },
  { num: 4, title: 'Skann QR og registrer garanti', text: 'Skann QR-koden i kittet med mobilen, fyll ut et kort skjema og motta garantibeviset på e-post.' },
];

const BENEFITS = [
  { icon: Shield, title: '10 års garanti', text: 'Samme garanti som vi gir for profesjonell oppfrisking — på selve beskyttelsen.' },
  { icon: Sparkles, title: 'Bevarer skriften', text: 'DC-Protect (flytende silisiumdioksid) hindrer at lakk og skrift visner over tid.' },
  { icon: Clock, title: 'Tar bare en time', text: 'Enkel prosess som de fleste klarer på under en time uten spesialverktøy.' },
  { icon: Award, title: 'Godkjent av fagfolk', text: 'Samme produkt og påføringsmetode som våre forhandlere bruker daglig.' },
];

export default function DIYKitLandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-emerald-900 to-emerald-700 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-emerald-100 hover:text-white mb-6 transition-colors"
            data-testid="diy-landing-back-btn"
          >
            <ArrowLeft size={20} /> Tilbake til forsiden
          </button>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-400/20 border border-amber-300/40 text-amber-200 rounded-full px-4 py-1.5 text-sm font-medium mb-5">
                <Sparkles size={16} /> Nytt — gjør-det-selv kit
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Beskytt gravsteinen selv —<br />
                <span className="text-amber-300">med 10 års garanti</span>
              </h1>
              <p className="text-lg text-emerald-100 mb-8 max-w-xl">
                Påfør samme profesjonelle DC-Protect beskyttelse som forhandlerne våre bruker.
                Bevar skrift og lakk, og få et offisielt garantibevis på e-post når du er ferdig.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <button
                  onClick={() => navigate('/bestill-kit')}
                  className="inline-flex items-center justify-center gap-2 bg-amber-400 text-stone-900 hover:bg-amber-300 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="diy-landing-cta-top"
                >
                  Bestill kit nå <ArrowRight size={20} />
                </button>
                <a
                  href="#hvordan"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:bg-white/10 rounded-full px-8 py-4 text-lg font-medium transition-colors"
                >
                  Slik fungerer det
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-emerald-100">
                <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-amber-300" /> Pris 1 500 kr inkl. mva</span>
                <span className="flex items-center gap-1.5"><Truck size={16} className="text-amber-300" /> Frakt 99 kr</span>
                <span className="flex items-center gap-1.5"><Shield size={16} className="text-amber-300" /> 10 års garanti</span>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 sm:p-8 shadow-2xl">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="text-stone-900" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-100 uppercase tracking-wide">Innhold i kittet</p>
                    <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Alt du trenger
                    </h3>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {KIT_CONTENTS.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-emerald-50">
                      <CheckCircle2 size={20} className="text-amber-300 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Hvorfor velge DIY-kittet?
            </h2>
            <p className="text-stone-600 text-lg">
              Du får samme profesjonelle resultat som vi gjør for forhandlerne våre — bare gjort av deg, hjemmefra.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-stone-50 rounded-xl p-6 hover:shadow-md transition-shadow border border-stone-100">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <b.icon className="text-emerald-800" size={22} />
                </div>
                <h3 className="font-semibold text-stone-900 text-lg mb-2">{b.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="hvordan" className="py-16 sm:py-20 bg-gradient-to-br from-stone-100 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Slik fungerer det
              </h2>
              <p className="text-stone-600 text-lg mb-8">
                Fire enkle steg fra du åpner pakken til garantibeviset ligger i innboksen din.
              </p>

              <ol className="space-y-5">
                {STEPS.map((s) => (
                  <li key={s.num} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-800 text-white rounded-full flex items-center justify-center text-lg font-bold">
                      {s.num}
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900 text-lg">{s.title}</h3>
                      <p className="text-stone-600">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 flex items-start gap-3">
                <QrCode className="text-amber-700 flex-shrink-0 mt-0.5" size={22} />
                <p className="text-sm text-stone-700">
                  <strong>Smart QR-registrering:</strong> Hvert kit har en unik QR-kode. Skann den med mobilkameraet etter påføring,
                  så åpnes garantiskjemaet med nummeret ferdig utfylt — du fyller bare inn navn, telefon og bilde.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src={STONE_IMG}
                alt="Eksempel på gravstein"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/5]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 max-w-xs hidden sm:block">
                <div className="flex items-center gap-2 mb-1">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-amber-400" fill="currentColor" />)}
                </div>
                <p className="text-sm text-stone-700 italic">
                  "Enkelt å bruke. Skriften ser like fin ut nå som etter oppfriskingen."
                </p>
                <p className="text-xs text-stone-500 mt-1">— Tilfreds kunde</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing card */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-2xl shadow-2xl p-8 sm:p-12">
            <div className="text-center mb-8">
              <p className="text-amber-300 text-sm uppercase tracking-widest font-semibold mb-2">Komplett DIY-kit</p>
              <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                1 500 kr
              </h2>
              <p className="text-stone-400">+ 99 kr i frakt · inkl. mva</p>
            </div>

            <ul className="space-y-3 mb-8 max-w-md mx-auto">
              {KIT_CONTENTS.map((item) => (
                <li key={item} className="flex items-center gap-3 text-stone-200">
                  <CheckCircle2 size={20} className="text-amber-400 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
              <li className="flex items-center gap-3 text-stone-200">
                <Shield size={20} className="text-amber-400 flex-shrink-0" />
                <span><strong>10 års garanti</strong> på beskyttelsen</span>
              </li>
            </ul>

            <button
              onClick={() => navigate('/bestill-kit')}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 text-stone-900 hover:bg-amber-300 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="diy-landing-cta-bottom"
            >
              Bestill kit nå <ArrowRight size={20} />
            </button>
            <p className="text-center text-stone-400 text-xs mt-4">
              Ingen forhåndsbetaling. Vi sender deg faktura eller Vipps-krav etter bestilling.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-stone-900 text-center mb-10" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ofte stilte spørsmål
          </h2>
          <div className="space-y-4">
            <FAQ q="Hvor lenge varer beskyttelsen?" a="DC-Protect beskytter i minst 10 år, og du får et offisielt garantibevis fra Gravsteinoppfriskeren som dokumenterer dette." />
            <FAQ q="Hva om jeg gjør noe feil?" a="Kittet kommer med trinn-for-trinn veiledning. Hvis du er usikker, ring oss eller send en e-post — vi hjelper deg gjerne." />
            <FAQ q="Når kommer pakken?" a="Vi sender kittet så snart betalingen er registrert. Vanligvis er det fremme innen 3–5 virkedager." />
            <FAQ q="Kan jeg bruke kittet på alle gravsteiner?" a="DC-Protect fungerer best på lakkerte ikoner og skrift på rene gravsteiner. Hvis steinen trenger oppfrisking først, anbefaler vi profesjonell behandling." />
            <FAQ q="Hva om kittet ikke fungerer for meg?" a="Ta kontakt så finner vi en løsning sammen. Vi står for kvaliteten på beskyttelsen." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-emerald-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Klar til å beskytte gravsteinen?
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Bestill kittet på under et minutt. Vi sender det rett hjem til deg.
          </p>
          <button
            onClick={() => navigate('/bestill-kit')}
            className="inline-flex items-center justify-center gap-2 bg-amber-400 text-stone-900 hover:bg-amber-300 rounded-full px-10 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            data-testid="diy-landing-cta-final"
          >
            Bestill kit nå <ArrowRight size={20} />
          </button>
        </div>
      </section>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="bg-white rounded-xl border border-stone-200 group">
      <summary className="flex items-center justify-between cursor-pointer p-5 font-semibold text-stone-900 list-none">
        <span>{q}</span>
        <span className="text-emerald-700 text-2xl group-open:rotate-45 transition-transform select-none">+</span>
      </summary>
      <div className="px-5 pb-5 text-stone-600 leading-relaxed">{a}</div>
    </details>
  );
}
